/**
 * threadManageAuthority — who may change a shared thread's people and Prest0n
 * (2026-09-08, chat-thread experience plan PR-1).
 *
 * ONE predicate, mirrored from the server's `actorCanManageThread`
 * (functions/src/prest0nSession/threadMembershipWrite.ts): the thread's
 * creator, a workspace owner, or a holder of `threads.manage` (Owner and
 * Admin by template). The client uses it ONLY to shape the UI — a member
 * without the permission sees the people list and a refusal line, never a
 * button that fails after the click — and the server stays the authority on
 * every write.
 *
 * Fail-open where the client cannot know: a thread with no registry row is
 * the viewer's own (nobody else is in it yet), and an RBAC walk still in
 * flight never reads as "no permission" (UNRESOLVED ≠ DENIED, 2026-08-19).
 */
export type ThreadManageAuthorityReason =
  | 'creator'
  | 'owner'
  | 'threads-manage'
  | 'unregistered'
  | 'rbac-unresolved'
  | 'not-permitted';

export interface ThreadManageAuthorityInput {
  selfId: string | null | undefined;
  /** The registry row's creator uid; null/absent when the row carries none. */
  createdBy?: string | null;
  /** True when the membership read returned a row for this thread. */
  registered: boolean;
  isOwner: boolean;
  hasThreadsManage: boolean;
  /** False while the viewer's RBAC walk is loading or ended in an error. */
  rbacResolved: boolean;
}

export interface ThreadManageAuthority {
  canManage: boolean;
  reason: ThreadManageAuthorityReason;
}

/** The catalog key the server checks (Owner/Admin by template). */
export const THREADS_MANAGE_PERMISSION = 'threads.manage';

export function resolveThreadManageAuthority(input: ThreadManageAuthorityInput): ThreadManageAuthority {
  const self = (input.selfId ?? '').trim();
  const creator = (input.createdBy ?? '').trim();
  if (!input.registered) return { canManage: true, reason: 'unregistered' };
  if (self && creator && creator === self) return { canManage: true, reason: 'creator' };
  if (input.isOwner) return { canManage: true, reason: 'owner' };
  if (input.hasThreadsManage) return { canManage: true, reason: 'threads-manage' };
  if (!input.rbacResolved) return { canManage: true, reason: 'rbac-unresolved' };
  return { canManage: false, reason: 'not-permitted' };
}
